/* eslint-disable no-underscore-dangle */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Alert, Badge, Button, ButtonGroup, Card, Form, Spinner,
} from 'react-bootstrap';
import Error from '../../Components/Error';
import { OvertimeReviewRow, ReceiptReviewRow } from '../../Components/TripClaims/TripClaimRows';
import CompleteBookingOffer from '../../Components/TripClaims/CompleteBookingOffer';
import ReceiptPhotoModal from '../../Components/TripClaims/ReceiptPhotoModal';
import OrderDetailsModal from '../Orders/Components/OrderDetailsModal';
import TripClaimsService from '../../Services/TripClaimsService';
import OperatorService from '../../Services/OperatorService';
import BookingService from '../../Services/BookingService';
import useModalState from '../../Hooks/useModalState';
import { PHOTO_URL_FRESH_MS } from '../../Hooks/useBookingClaims';
import { lineItemPrefillFor } from '../Orders/Functions/tripClaimLineItems';
import { formatDateTime } from '../../Utils/timezone';
import {
  countTripClaims, driverEventsLink, NO_TRIP_CLAIMS, tripClaimsMessage,
} from '../../Utils/tripClaims';
import { canOfferCompletion } from '../../Utils/completeAfterClaims';

const TYPES = [
  { value: '', label: 'All' },
  { value: 'receipt', label: 'Receipts' },
  { value: 'overtime', label: 'Overtime' },
];

const errorText = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

/** The booking a row belongs to, as the shared rows show it on a cross-booking list. */
const tripOf = (claim) => ({
  bookingId: claim.booking_id,
  reference: claim.booking_reference,
  tripDate: claim.trip_date,
  status: claim.booking_status,
});

/** Claims with no booking at all are their own bucket, not one booking's. */
const UNTIED = '';

/**
 * The claims of one booking, together, in the order the API listed them —
 * and so the bookings themselves in the order their first claim arrived,
 * which is oldest trip first.
 *
 * A QUEUE IS WORKED BOOKING BY BOOKING, not claim by claim. A flat list
 * scatters the two receipts and the overtime of one trip among everybody
 * else's, so "how much is left on this one" — the only question that decides
 * whether a booking can be closed — could not be answered without scrolling
 * the whole page and remembering.
 */
export function groupByBooking(claims) {
  const groups = [];
  const byId = new Map();

  (claims || []).forEach((claim) => {
    const id = claim.booking_id || UNTIED;
    let group = byId.get(id);
    if (!group) {
      group = {
        bookingId: claim.booking_id || null,
        reference: claim.booking_reference,
        tripDate: claim.trip_date,
        status: claim.booking_status,
        claims: [],
      };
      byId.set(id, group);
      groups.push(group);
    }
    // The booking's own fields come from whichever row carries them; a later
    // row may know the status when an earlier one did not.
    group.reference = group.reference || claim.booking_reference;
    group.tripDate = group.tripDate || claim.trip_date;
    group.status = claim.booking_status || group.status;
    group.claims.push(claim);
  });

  return groups;
}

/**
 * How many of a group's claims are still undecided — what is left ON SCREEN.
 *
 * Display only, and deliberately NOT the rule for offering to complete the
 * booking: that one is the server's blocking subset, read off the booking
 * after the write (Utils/completeAfterClaims). These two answer different
 * questions and are allowed to differ — a claim filed before the API's
 * TRIP_CLAIMS_BLOCK_FROM still waits for staff here while blocking nothing
 * there — so neither is derived from the other.
 */
export const stillWaiting = (group) => countTripClaims(
  group.claims.filter((claim) => claim.kind === 'receipt').map((claim) => claim.expense),
  group.claims.filter((claim) => claim.kind === 'overtime').map((claim) => claim.overtime),
).total;

/** The booking heading a group of claims, with how much of it is left. */
function BookingGroupHeader({ group }) {
  const left = stillWaiting(group);

  return (
    <div
      className="d-flex flex-wrap align-items-center gap-2 bg-light border-bottom px-2 py-2"
      data-testid="trip-claims-booking-group"
      data-booking-id={group.bookingId || ''}
      data-waiting={left}
    >
      <i className="fa fa-car text-muted" />
      {group.bookingId ? (
        <Link to={driverEventsLink(group.bookingId)} className="fw-bold">
          {group.reference || 'Booking'}
        </Link>
      ) : (
        <span className="fw-bold text-muted">Not tied to a trip</span>
      )}
      {group.tripDate && (
        <span className="small text-muted">{`trip ${formatDateTime(group.tripDate)}`}</span>
      )}
      {group.status && (
        <Badge bg="light" text="dark" className="border" data-testid="trip-claims-group-status">
          {group.status}
        </Badge>
      )}
      <span className="ms-auto">
        {left > 0 ? (
          <Badge bg="warning" text="dark" data-testid="trip-claims-group-remaining">
            {`${left} waiting`}
          </Badge>
        ) : (
          <Badge bg="success" data-testid="trip-claims-group-clear">Clear</Badge>
        )}
      </span>
    </div>
  );
}

/**
 * Trip claims — every receipt and overtime entry drivers and operator logins
 * filed that staff have not yet approved or rejected, across bookings,
 * grouped by booking, oldest trip first. Rows are the booking's Driver events
 * rows; approve / reject goes through the same review path, and "Create line
 * item" through the same prefill.
 *
 * A decided row stays on screen (so its line item can be created) until the
 * list is re-read. `?booking=<id>` narrows the list to one booking — where
 * a refused "Mark Completed" links to.
 *
 * Once a decision leaves a booking with nothing BLOCKING it, the page offers
 * to close the booking there and then rather than leave it for a nightly job
 * two days out. The offer is made after the decision is committed, never with
 * it, and declining it is silent and final.
 */
function TripClaims() {
  const [searchParams, setSearchParams] = useSearchParams();
  const bookingId = searchParams.get('booking') || '';
  const [filters, setFilters] = useState({
    type: '', from: '', to: '', operatorId: '',
  });
  const [claims, setClaims] = useState([]);
  const [counts, setCounts] = useState(NO_TRIP_CLAIMS);
  const [capped, setCapped] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [operators, setOperators] = useState([]);
  const [offer, setOffer] = useState(null);
  const [completedNote, setCompletedNote] = useState('');
  const [photo, setPhoto] = useState(null);
  const fetchedAtRef = useRef(0);
  // Bookings the offer has already been declined for. Declining is final:
  // a second prompt for the same booking would be exactly the nagging the
  // offer is meant not to be.
  const declinedRef = useRef(new Set());
  const lineItemModal = useModalState();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await TripClaimsService.list({
        ...filters, bookingId: bookingId || undefined,
      });
      setClaims(result.claims);
      setCounts(result.counts);
      setCapped(result.capped);
      fetchedAtRef.current = Date.now();
      return result.claims;
    } catch (err) {
      setError(errorText(err, 'Could not load the trip claims'));
      return [];
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, [filters, bookingId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    OperatorService.fetchOperators()
      .then((response) => setOperators(response?.data?.data || []))
      .catch(() => setOperators([]));
  }, []);

  const groups = useMemo(() => groupByBooking(claims), [claims]);

  const setFilter = (key) => (event) => {
    const { value } = event.target;
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // A decision updates the row in place; the counts follow.
  const settle = (claim, patch) => {
    const same = (c) => c.kind === claim.kind && c.id === claim.id;
    setClaims((prev) => prev.map((c) => (same(c) ? { ...c, ...patch } : c)));
    setCounts((prev) => ({
      receipts: prev.receipts - (claim.kind === 'receipt' ? 1 : 0),
      overtime: prev.overtime - (claim.kind === 'overtime' ? 1 : 0),
      total: prev.total - 1,
    }));
  };

  /**
   * Ask the API whether this booking is now clear, and offer to close it if
   * it is. Called only AFTER a decision has been committed, so it cannot
   * affect one: the booking is re-read fresh (its `trip_claims.blocking` is
   * the server's own answer, not a count of what is on screen), and if the
   * read fails the offer is simply not made. A missed offer costs the nightly
   * job; a wrong one would cost a booking.
   */
  const offerCompletion = async (claimBookingId) => {
    if (!claimBookingId || declinedRef.current.has(claimBookingId)) return;
    try {
      const order = await BookingService.getBookingById(claimBookingId);
      if (canOfferCompletion(order)) setOffer(order);
    } catch {
      // The decision stands either way. Nothing to say.
    }
  };

  const review = async (claim, decision, orderLine) => {
    setBusyId(claim.id);
    setError('');
    try {
      if (claim.kind === 'receipt') {
        const updated = await TripClaimsService.reviewReceipt(claim.id, decision, orderLine);
        const status = updated?.status || decision;
        settle(claim, { expense: { ...claim.expense, ...(updated || {}), status } });
      } else {
        const updated = await TripClaimsService
          .reviewOvertime(claim.booking_id, claim.id, decision);
        const reviewStatus = updated?.review_status || decision;
        settle(claim, {
          overtime: { ...claim.overtime, ...(updated || {}), review_status: reviewStatus },
        });
      }
      // Committed. Only now is the close worth offering.
      await offerCompletion(claim.booking_id);
    } catch (err) {
      setError(errorText(err, 'Could not record the decision'));
    } finally {
      setBusyId(null);
    }
  };

  const declineOffer = () => {
    if (offer?._id) declinedRef.current.add(offer._id);
    setOffer(null);
  };

  /**
   * The booking is Completed. The decided rows stay where they are — their
   * "Create line item" is still the reason the queue keeps them — so only the
   * booking's own status is brought up to date, in the group header the
   * person is already looking at.
   */
  const onCompleted = (order) => {
    setClaims((prev) => prev.map((claim) => (
      claim.booking_id === order._id ? { ...claim, booking_status: 'Completed' } : claim
    )));
    setCompletedNote(`${order.booking_reference || 'The booking'} is now Completed.`);
    setOffer(null);
  };

  // "Create line item" needs the whole booking for the adjustments form, in
  // its Order Details' Order Ledger tab.
  const createLineItem = async (claim) => {
    setError('');
    try {
      const order = await BookingService.getBookingById(claim.booking_id);
      if (!order) {
        setError('The booking could not be loaded');
        return;
      }
      const lineItemClaim = claim.kind === 'receipt'
        ? { kind: 'receipt', expense: claim.expense }
        : { kind: 'overtime', entry: claim.overtime };
      lineItemModal.show({ order, lineItem: lineItemPrefillFor(order, lineItemClaim) });
    } catch (err) {
      setError(errorText(err, 'Could not open the adjustments form'));
    }
  };

  // Presigned photo links are short-lived: re-read the list first when stale,
  // then show the photo over the queue rather than in a new tab.
  const openPhoto = async (event, expense) => {
    event.preventDefault();
    let url = expense.photo_url;
    if (Date.now() - fetchedAtRef.current >= PHOTO_URL_FRESH_MS) {
      const rows = await load();
      url = rows.find((c) => c.kind === 'receipt' && c.id === expense._id)?.expense?.photo_url;
    }
    if (url) setPhoto({ url, expense });
  };

  return (
    <>
      <h1 className="page-title">
        Trip Claims
        {counts.total > 0 && (
          <Badge bg="warning" text="dark" className="ms-2 align-middle fs-6">{counts.total}</Badge>
        )}
      </h1>
      <p className="text-muted">
        Receipts and overtime drivers and operators filed, waiting for review. A booking
        can&apos;t be completed — by hand or by the nightly job — until each of its claims is
        approved or rejected.
      </p>

      <Card className="mb-3">
        <Card.Body className="d-flex flex-wrap gap-3 align-items-end">
          <Form.Group>
            <Form.Label className="small text-muted mb-1 d-block">Type</Form.Label>
            <ButtonGroup size="sm" aria-label="Claim type">
              {TYPES.map((t) => (
                <Button
                  key={t.value || 'all'}
                  variant={filters.type === t.value ? 'primary' : 'outline-secondary'}
                  onClick={() => setFilters((prev) => ({ ...prev, type: t.value }))}
                  aria-pressed={filters.type === t.value}
                >
                  {t.label}
                </Button>
              ))}
            </ButtonGroup>
          </Form.Group>
          <Form.Group controlId="trip-claims-from">
            <Form.Label className="small text-muted mb-1">Trip from</Form.Label>
            <Form.Control size="sm" type="date" value={filters.from} onChange={setFilter('from')} />
          </Form.Group>
          <Form.Group controlId="trip-claims-to">
            <Form.Label className="small text-muted mb-1">Trip to</Form.Label>
            <Form.Control size="sm" type="date" value={filters.to} onChange={setFilter('to')} />
          </Form.Group>
          <Form.Group controlId="trip-claims-operator" style={{ minWidth: 200 }}>
            <Form.Label className="small text-muted mb-1">Operator</Form.Label>
            <Form.Select size="sm" value={filters.operatorId} onChange={setFilter('operatorId')}>
              <option value="">All operators</option>
              {operators.map((op) => <option key={op.Id} value={op.Id}>{op.name}</option>)}
            </Form.Select>
          </Form.Group>
          <Button size="sm" variant="outline-secondary" onClick={load} aria-label="Refresh">
            <i className={`fa fa-sync ${loading ? 'fa-spin' : ''}`} />
          </Button>
        </Card.Body>
      </Card>

      {bookingId && (
        <div className="mb-3">
          <Badge bg="light" text="dark" className="border p-2" data-testid="trip-claims-booking-filter">
            {`One booking${claims[0]?.booking_reference ? `: ${claims[0].booking_reference}` : ''}`}
            <Button
              variant="link"
              size="sm"
              className="p-0 ms-2 align-baseline"
              aria-label="Show every booking"
              onClick={() => setSearchParams({})}
            >
              <i className="fa fa-times" />
            </Button>
          </Badge>
        </div>
      )}

      {completedNote && (
        <Alert
          variant="success"
          dismissible
          onClose={() => setCompletedNote('')}
          data-testid="trip-claims-completed-note"
        >
          {completedNote}
        </Alert>
      )}

      <Error error={error} setError={setError} />

      <Card>
        <Card.Header className="fw-bold d-flex align-items-center">
          <span>{counts.total > 0 ? tripClaimsMessage(counts) : 'Nothing waiting for review'}</span>
          {loading && <Spinner animation="border" size="sm" className="ms-2" />}
        </Card.Header>
        <Card.Body data-testid="trip-claims-list" data-loaded={loaded} className="p-0">
          {capped && (
            <p className="small text-muted p-2 mb-0" data-testid="trip-claims-capped">
              More claims are waiting than fit on one page — narrow it with the filters above.
            </p>
          )}
          {loaded && claims.length === 0 && (
            <p className="text-muted p-2 mb-0">No trip claims waiting for review.</p>
          )}
          {groups.map((group) => (
            <div key={group.bookingId || 'untied'} data-testid="trip-claims-group">
              <BookingGroupHeader group={group} />
              <div className="px-2">
                {group.claims.map((claim) => (claim.kind === 'receipt' ? (
                  <ReceiptReviewRow
                    key={`receipt-${claim.id}`}
                    expense={claim.expense}
                    trip={tripOf(claim)}
                    busy={busyId === claim.id}
                    onReview={(decision, orderLine) => review(claim, decision, orderLine)}
                    onCreateLineItem={claim.booking_id ? () => createLineItem(claim) : undefined}
                    onOpenPhoto={openPhoto}
                  />
                ) : (
                  <OvertimeReviewRow
                    key={`overtime-${claim.id}`}
                    entry={claim.overtime}
                    trip={tripOf(claim)}
                    busy={busyId === claim.id}
                    onReview={(decision) => review(claim, decision)}
                    onCreateLineItem={() => createLineItem(claim)}
                  />
                )))}
              </div>
            </div>
          ))}
        </Card.Body>
      </Card>

      {photo && (
        <ReceiptPhotoModal
          url={photo.url}
          expense={photo.expense}
          onClose={() => setPhoto(null)}
        />
      )}

      {offer && (
        <CompleteBookingOffer
          order={offer}
          onCompleted={onCompleted}
          onClose={declineOffer}
        />
      )}

      {lineItemModal.isVisible && (
        <OrderDetailsModal
          order={lineItemModal.selectedObject.order}
          show
          handleClose={lineItemModal.hide}
          initialTab="ledger"
          lineItem={lineItemModal.selectedObject.lineItem}
        />
      )}
    </>
  );
}

export default TripClaims;
